import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import { ColumnConfig, DisplayColumns } from "./types";

/**
 * Column show/hide state, persisted through the injected usecase getters/setters.
 * Drives the "Hide Column" popover (Select all + per-column group).
 *
 * checkAll / indeterminate are DERIVED from visibility (not stored) so they stay
 * correct after the initial load and after every toggle. Unchecking "Select all"
 * sets every column false → all hidden.
 */
export const useColumnVisibility = (
  configs: ColumnConfig[],
  getDisplayColumns: () => DisplayColumns,
  setDisplayColumns: (columns: DisplayColumns) => void,
) => {
  const [visibility, setVisibility] = useState<Partial<DisplayColumns>>({});

  const total = configs.length;

  useEffect(() => {
    setVisibility(getDisplayColumns());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyVisibility = useCallback(
    (resolve: (config: ColumnConfig) => boolean) => {
      const next: DisplayColumns = {};
      configs.forEach((config) => {
        next[config.key] = resolve(config);
      });
      setDisplayColumns(next);
      setVisibility(next);
    },
    [configs, setDisplayColumns],
  );

  const options = useMemo(
    () => configs.map((config) => config.title),
    [configs],
  );

  const values = useMemo<CheckboxValueType[]>(
    () =>
      configs
        .filter((config) => visibility[config.key])
        .map((config) => config.title),
    [configs, visibility],
  );

  const checkedCount = values.length;
  const checkAll = total > 0 && checkedCount === total;
  const indeterminate = checkedCount > 0 && checkedCount < total;

  const onDisplayColumnChanged = useCallback(
    (list: CheckboxValueType[]) => {
      applyVisibility((config) => list.includes(config.title));
    },
    [applyVisibility],
  );

  const onCheckAllChange = useCallback(
    (e: CheckboxChangeEvent) => {
      applyVisibility(() => e.target.checked);
    },
    [applyVisibility],
  );

  return {
    visibility,
    indeterminate,
    checkAll,
    options,
    values,
    onCheckAllChange,
    onDisplayColumnChanged,
  };
};
